import { ChevronLeftIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Container,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Link,
    useToast,
} from '@chakra-ui/react';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import {
    LoaderFunctionArgs,
    Link as ReactRouterLink,
    useLoaderData,
    useNavigate,
} from 'react-router-dom';
import { fetchStock } from '../../api/repository/stock';
import { createStockDetailRoute } from '../../constants/routes';
import { convertStockItemFromApi, findStock } from '../../helpers/stock';
import { useUpdateStock } from '../../hooks/useUpdateStock';
import { StockItemInterface } from '../../types/stock';
import {convertAppointmentFromApi} from "../../helpers/appointment";
import {fetchAppointments} from "../../api/repository/appointment";

export const loader = async ({ params }: LoaderFunctionArgs) => {
    const { id: stockNumber } = params;
    const stock = await fetchStock();
    const appointments = await fetchAppointments();

    const formattedAppointments = appointments.map((item) =>
        convertAppointmentFromApi(item)
    );

    const formattedStock = stock?.map((item) => convertStockItemFromApi(item, formattedAppointments));

    const currentStock = stockNumber
        ? findStock(formattedStock, stockNumber)
        : null;

    if (!currentStock) {
        throw new Response('Not Found', { status: 404 });
    }

    return { currentStock };
};

export const StockUpdate: FC = () => {
    const { currentStock } = useLoaderData() as {
        currentStock: StockItemInterface;
    };
    const navigate = useNavigate();
    const { mutateAsync, isPending } = useUpdateStock();
    const toast = useToast();

    const { register, handleSubmit } = useForm({
        defaultValues: {
            customerNote: currentStock.customerNote ?? '',
        },
    });

    const detailRoute = createStockDetailRoute(currentStock.stockNumber);

    const onSubmit = async (values: { customerNote: string }) => {
        try {
            await mutateAsync({
                stockNumber: currentStock.stockNumber,
                stock: {
                    customerNote: values.customerNote,
                },
            });

            navigate(detailRoute);

            toast({
                title: 'Gelukt! We hebben je voertuig in goede orde ontvangen.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Er ging iets mis met het updaten van je voertuig.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }
    };

    return (
        <Box px={8} py={8}>
            <Link
                as={ReactRouterLink}
                to={detailRoute}
                mb={2}
                display={'inline-block'}
            >
                <ChevronLeftIcon />
                Terug naar voertuig
            </Link>
            <Container px={0}>
                <Heading mb={6} color={'secondary'}>
                    {`Voertuig ${currentStock.stockNumber} aanpassen`}
                </Heading>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormControl mb={4} isDisabled={isPending}>
                        <FormLabel>Klant notitie</FormLabel>
                        <Input {...register('customerNote')} />
                    </FormControl>

                    <Button
                        type={'submit'}
                        colorScheme={'primary'}
                        isLoading={isPending}
                    >
                        Voertuig aanpassen
                    </Button>
                </form>
            </Container>
        </Box>
    );
};
